import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { ModulesService } from '@shared/services/modules.service';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AccessLogsService } from '@shared/services/entities/accessLogs.service';
import { SimpleExcelService } from '@shared/services/simple-excel.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';

/**
 * Historial de acceso: qué usuarios entran a la plataforma y cuándo.
 * Los registros los crea el login (uno por sesión iniciada).
 */
import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
@Component({
  selector: 'app-access-history',
  standalone: true,
  imports: [ MatButtonToggleModule,PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    FormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './access-history.component.html',
  styleUrls: ['../audit-page.scss', './access-history.component.scss'],
})
export class AccessHistoryComponent implements OnInit {
  /** Título = etiqueta visible del módulo (tabla `modules`). */
  readonly pageLabel = inject(ModulesService).moduleTitle('/audit/access', 'Historial de acceso');

  private readonly accessLogsService   = inject(AccessLogsService);
  private readonly simpleExcel         = inject(SimpleExcelService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly router              = inject(Router);

  loading = signal(false);
  readonly listView = new ListView('audit-access', 'table');

  userFilter = '';
  startDate: Date | null = null;
  endDate: Date | null = null;

  allLogs: any[] = [];
  // El correo va dentro de la celda de Usuario (una columna menos, más legible).
  columns = ['createdOn', 'name', 'roleName', 'userAgent'];
  dataSource = new MatTableDataSource<any>([]);

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.load();
  }

  async load(): Promise<void> {
    this.loading.set(true);
    try {
      const response: any = await this.accessLogsService.search({ searchType: 'default', condition: { deleted: false } });
      this.allLogs = ((response?.data as any[]) ?? []).filter(x => x)
        .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
      this.applyFilters();
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo cargar el historial de acceso.', dismissBtn: true,
      });
    } finally {
      this.loading.set(false);
    }
  }

  applyFilters(): void {
    const needle = this.userFilter.trim().toLowerCase();
    this.dataSource.data = this.allLogs
      .filter(log => !needle
        || String(log.name ?? '').toLowerCase().includes(needle)
        || String(log.email ?? '').toLowerCase().includes(needle)
        || String(log.roleName ?? '').toLowerCase().includes(needle))
      .filter(log => this.inRange(log.createdOn));
  }

  private inRange(createdOn: string): boolean {
    if (!createdOn) return true;
    const d = new Date(createdOn).getTime();
    if (this.startDate && d < this.startDate.getTime()) return false;
    if (this.endDate) {
      const end = new Date(this.endDate); end.setHours(23, 59, 59, 999);
      if (d > end.getTime()) return false;
    }
    return true;
  }

  /** El user agent completo es ruido: se resume a navegador + sistema. */
  browserOf(userAgent: string): string {
    const ua = String(userAgent ?? '');
    if (!ua) return '—';
    const browser =
      /Edg\//.test(ua) ? 'Edge' :
      /OPR\//.test(ua) ? 'Opera' :
      /Chrome\//.test(ua) ? 'Chrome' :
      /Safari\//.test(ua) ? 'Safari' :
      /Firefox\//.test(ua) ? 'Firefox' : 'Otro';
    const os =
      /Windows/.test(ua) ? 'Windows' :
      /Mac OS X/.test(ua) ? 'macOS' :
      /Android/.test(ua) ? 'Android' :
      /iPhone|iPad/.test(ua) ? 'iOS' :
      /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} · ${os}` : browser;
  }

  formatDate(value: string): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  }

  /** Fecha y hora por separado (en teléfono cada una en su renglón). */
  datePart(value: string): string {
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value ?? '') : d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  timePart(value: string): string {
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  }

  async downloadExcel(): Promise<void> {
    const logs = this.dataSource.data;
    if (logs.length === 0) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'No hay accesos que descargar.', duration: 2000 });
      return;
    }
    await this.simpleExcel.simpleXLSX({
      fileName: 'Historial de acceso',
      sheets: [{
        sheetName: 'Accesos',
        header: [],
        lineBreak: 0,
        columns: [
          { columnKey: '_date',     columnName: 'Fecha y hora' },
          { columnKey: 'name',      columnName: 'Usuario' },
          { columnKey: 'email',     columnName: 'Correo' },
          { columnKey: 'roleName',  columnName: 'Rol' },
          { columnKey: '_browser',  columnName: 'Navegador' },
        ],
        data: logs.map(log => ({
          ...log,
          _date: this.formatDate(log.createdOn),
          _browser: this.browserOf(log.userAgent),
        })),
      }],
    });
  }

  /** Indicadores del rango filtrado. */
  get kpis() {
    const logs = this.dataSource.data;
    const users = new Set(logs.map(l => l.email || l.name).filter(x => !!x));
    const browsers = new Map<string, number>();
    for (const l of logs) { const b = this.browserOf(l.userAgent).split(' · ')[0]; browsers.set(b, (browsers.get(b) ?? 0) + 1); }
    const topBrowser = [...browsers.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
    return {
      total: logs.length,
      users: users.size,
      last: logs[0] ? this.formatDate(logs[0].createdOn) : '—',
      topBrowser,
    };
  }

  get filterChips(): FilterChip[] {
    if (!this.startDate && !this.endDate) return [];
    const f = (d: any) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '…';
    return [{ key: 'date', label: 'Periodo', value: f(this.startDate) + ' – ' + f(this.endDate) }];
  }
  removeChip(key: string): void {
    if (key === 'date') { this.startDate = null as any; this.endDate = null as any; this.applyFilters(); }
  }

  clearFilters(): void {
    this.userFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  goBack(): void { this.router.navigate(['/audit/menu']); }
}
